<template>
    <v-card>
        <v-card-title>
            <v-row justify="space-between">
                <v-col cols="2">Filters</v-col>
                <v-spacer />
                <v-col cols="10" class="text-right">
                    <v-btn class="mr-4" color="primary" @click="clearFilters">Clear Filters</v-btn>
                    <v-btn class="mr-4" color="primary" @click="toggleFilters">
                        {{ toggleFiltersButtonText }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-card-text v-show="showFilters">
            <v-container fluid style="display: flex; flex-wrap: wrap">
                <v-row dense>
                    <v-col
                        v-for="filterName in filterNames"
                        :key="filterName"
                        cols="12"
                        sm="6"
                        md="3"
                        lg="2"
                        xl="2"
                        class="mx-1"
                    >
                        <v-autocomplete
                            v-model="filters[filterName]"
                            :items="filterOptions[filterName].values"
                            :label="formatFilterName(filterName)"
                            density="compact"
                            variant="outlined"
                            clearable
                            multiple
                            class="no-text-field-details"
                        />
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>
    </v-card>
</template>

<script lang="ts" setup>
import { computed, reactive, watch, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import _ from "lodash";
import { FilterOptions, Filters } from "@/lib/filterUtils";

const props = withDefaults(defineProps<{
    filterOptions: FilterOptions;
    collapsed?: boolean;
}>(), {
    collapsed: false,
});

const emit = defineEmits<{
    (e: "update:modelValue", value: Filters): void;
}>();

const route = useRoute();
const router = useRouter();

const filters = reactive<Record<string, string[]>>({});

const showFilters = ref(true);

const filterNames = computed(() => Object.keys(props.filterOptions));
const toggleFiltersButtonText = computed(() =>
    showFilters.value ? "Hide Filters" : "Show Filters"
);

const formatFilterName = (filterName: string) =>
    _.startCase(
        filterName.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2").replace(/([a-z\d])([A-Z])/g, "$1 $2")
    );

function clearFilters() {
    for (const key of filterNames.value) {
        filters[key] = [];
    }
    updateRouteQuery();
}

function toggleFilters() {
    showFilters.value = !showFilters.value;
}

function updateRouteQuery() {
    const query = { ...route.query };
    for (const key of filterNames.value) {
        if (filters[key]?.length) {
            query[key] = filters[key];
        } else {
            delete query[key];
        }
    }
    router.replace({ query });
}

function syncFiltersFromQuery() {
    for (const key of filterNames.value) {
        const queryVal = route.query[key];
        filters[key] = Array.isArray(queryVal)
            ? queryVal.filter((val): val is string => val !== null)
            : queryVal
              ? [queryVal as string]
              : [];
    }
}

function applyDefaultFilters() {
    if (!isFiltersActive()) {
        for (const filterName of filterNames.value) {
            const defaults = props.filterOptions[filterName].defaults;
            if (!_.isNil(defaults)) {
                filters[filterName] = defaults;
            }
        }
    }
}

function isFiltersActive(): boolean {
    for (const key of Object.keys(filters)) {
        if (filters[key].length > 0) return true;
    }
    return false;
}

onMounted(() => {
    syncFiltersFromQuery();
    applyDefaultFilters();
    showFilters.value = !props.collapsed;
    emit("update:modelValue", _.cloneDeep(filters));
});

watch(
    () => filters,
    (newFilters) => {
        updateRouteQuery();
        emit("update:modelValue", _.cloneDeep(newFilters));
    },
    { deep: true }
);
</script>

<style scoped>
.no-text-field-details:deep(.v-input__details) {
    display: none;
}
</style>
